import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import type {ColumnDef} from "@tanstack/react-table";
import ProfileListAiAction from "crqlar-react-common/dist/components/profile/ProfileListAiAction";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {ProfileListItem} from "crqlar-react-common/dist/models/profile/profileListItem.model";
import type {ProfileListRequestDTO} from "crqlar-react-common/dist/models/profile/profileListRequest.dto";
import {ProfileAggregationService} from "crqlar-react-common/dist/services/aggregation/profileAggregation.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {convertLocalDateToDate, endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableExport,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
    useServerTableContext
} from "../../common/serverTable";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import SettingsListPage from "../layout/SettingsListPage";
import {ProfileStayPeriodFilter} from "./ProfileStayPeriodFilter";
import type {ProfileStayPeriod} from "./ProfileStayPeriodFilter";
import {DateElement} from "../../common/uiElements/DateElement";
import {CountryElement} from "../../common/uiElements/CountryElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {NationalityElement} from "../../common/uiElements/NationalityElement";
import {AccommodationElement} from "../../common/uiElements/ResourceElement";
import {SimpleChipElement} from "../../common/uiElements/SimpleChipElement";
import {StayStatusElement} from "../../common/uiElements/StayStatusElement";

const textList = (values: Array<string | undefined>) => values.filter((value): value is string => !!value);

const localizedInterestValues = (profile: ProfileListItem, locale: string) => (
    textList(profile.interests.map(interest => interest.localizations?.[locale] ?? interest.value))
);


const useProfileListColumns = (readOnlyUser: boolean): ColumnDef<ProfileListItem>[] => {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;

    return useMemo<ColumnDef<ProfileListItem>[]>(() => {
        const columns: ColumnDef<ProfileListItem>[] = [
            {
                id: 'actions',
                header: t('Actions'),
                cell: function ProfileAiActions({row}) {
                    const {query} = useServerTableContext<ProfileListItem>();

                    return (
                        <ProfileListAiAction
                            profile={row.original}
                            refreshList={() => query.refetch()}
                        />
                    );
                },
                size: 88,
                enableResizing: false,
                meta: {defaultActive: false, align: 'center'}
            },
            {
                id: 'name',
                header: t('Name'),
                accessorFn: profile => formatProfileListName(profile),
                cell: ({getValue}) => <Box>{getValue<string>()}</Box>,
                size: 240,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    grow: true,
                    printable: true,
                    printDefault: true
                }
            },
            {
                id: 'lastNameDetails',
                header: t('Last name'),
                accessorFn: profile => profile.lastName,
                cell: ({row}) => <Box>{row.original.lastName ?? ''}</Box>,
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'lastName',
                    printable: true,
                }
            },
            {
                id: 'firstNameDetails',
                header: t('First name'),
                accessorFn: profile => profile.firstName,
                cell: ({row}) => <Box>{row.original.firstName ?? ''}</Box>,
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'firstName',
                    printable: true,
                }
            },
            {
                id: 'age',
                header: t('Age'),
                accessorFn: profile => {
                    const birthDate = convertLocalDateToDate(profile.birthDay);
                    if (!birthDate) return undefined;

                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const birthdayThisYear = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());

                    if (today < birthdayThisYear) age -= 1;
                    return age >= 0 ? age : undefined;
                },
                cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? ''}</Box>,
                size: 100,
                meta: {
                    defaultActive: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => row.getValue<number | undefined>('age') ?? ''
                }
            },
            {
                id: 'profileType',
                header: t('Profile type'),
                accessorFn: profile => profile.profileType,
                cell: ({row}) =>
                    <Box>{row.original.profileType ? t(`Profile type ${row.original.profileType}`) : ''}</Box>,
                size: 180,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'profileTypeC',
                    printable: true,
                    printCell: row => row.original.profileType ? t(`Profile type ${row.original.profileType}`) : ''
                }
            },
            {
                id: 'profileStatus',
                header: t('Profile status'),
                accessorFn: profile => profile.profileStatus,
                cell: ({row}) =>
                    <Box>{row.original.profileStatus ? t(`Profile status ${row.original.profileStatus}`) : ''}</Box>,
                size: 170,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'statusCodeC',
                    printable: true,
                    printCell: row => row.original.profileStatus ? t(`Profile status ${row.original.profileStatus}`) : ''
                }
            },
            {
                id: 'stayStatus',
                header: t('Stay status'),
                accessorFn: profile => profile.stayStatus,
                cell: ({row}) => (
                    <StayStatusElement
                        stayStatus={row.original.stayStatus}
                        startDate={row.original.selectedStay?.startDay}
                        endDate={row.original.selectedStay?.endDay}
                    />
                ),
                size: 170,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: 'activeStays.status',
                    printable: true,
                    printDefault: true,
                    printCell: row => <StayStatusElement stayStatus={row.original.stayStatus} asPrint/>
                }
            },
            {
                id: 'smartCategories',
                header: t('Smart Categories'),
                accessorFn: profile => textList(profile.smartCategories.map(category => category.text)),
                cell: ({getValue}) => {
                    const labels = getValue<string[]>();
                    return labels.length ? (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5}}>
                            {labels.map(label => <Chip key={label} label={label} size="small" variant="outlined"/>)}
                        </Box>
                    ) : null;
                },
                size: 230,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.categories.text',
                    printable: true,
                    printCell: row => row.getValue<string[]>('smartCategories').join(', ')
                }
            },
            {
                id: 'interests',
                header: t('Interests'),
                accessorFn: profile => localizedInterestValues(profile, locale),
                cell: ({row}) => (
                    <Box data-no-row-click sx={{py: 0.5}}>
                        <SimpleChipElement values={localizedInterestValues(row.original, locale)}/>
                    </Box>
                ),
                size: 250,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.interests.interest',
                    printable: true,
                    printCell: row => (
                        <SimpleChipElement values={localizedInterestValues(row.original, locale)} asPrint/>
                    )
                }
            },
            {
                id: 'recommendations',
                header: t('Recommendations'),
                accessorFn: profile => profile.hasRecommendations,
                cell: ({row}) => row.original.hasRecommendations
                    ? <Chip label={t('New recommendations!')} size="small" color="primary" variant="outlined"/>
                    : null,
                size: 210,
                meta: {
                    defaultActive: false,
                    printable: true,
                    printCell: row => row.original.hasRecommendations ? t('New recommendations!') : ''
                }
            },
            {
                id: 'gender',
                header: t('Gender'),
                accessorFn: profile => profile.gender,
                cell: ({row}) => <Box>{row.original.gender ? t(`Gender type ${row.original.gender}`) : ''}</Box>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => row.original.gender ? t(`Gender type ${row.original.gender}`) : ''
                }
            },
            {
                id: 'nationality',
                header: t('Nationality'),
                accessorFn: profile => profile.nationality,
                cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
                size: 130,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>
                }
            },
            {
                id: 'countryCode',
                header: t('Country'),
                accessorFn: profile => profile.countryCode,
                cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
                size: 130,
                meta: {
                    defaultActive: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>
                }
            },
            {
                id: 'birthDay',
                header: t('Birthday'),
                accessorFn: profile => profile.birthDay,
                cell: ({row}) => <DateElement date={row.original.birthDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.birthDay} dateOnly/>
                }
            },
            {
                id: 'room',
                header: t('Room'),
                accessorFn: profile => profile.selectedStay?.roomId,
                cell: ({row}) => row.original.selectedStay?.roomId
                    ? (
                        <Box data-no-row-click>
                            <AccommodationElement name={row.original.selectedStay.roomId}/>
                        </Box>
                    )
                    : null,
                size: 130,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: 'activeStays.roomId',
                    printable: true,
                    printDefault: true,
                    printCell: row => row.original.selectedStay?.roomId
                        ? <AccommodationElement name={row.original.selectedStay.roomId} asPrint/>
                        : null
                }
            },
            {
                id: 'arrival',
                header: t('Arrival'),
                accessorFn: profile => profile.selectedStay?.startDay,
                cell: ({row}) => <DateElement date={row.original.selectedStay?.startDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: 'activeStays.startDay',
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.selectedStay?.startDay} dateOnly/>
                }
            },
            {
                id: 'departure',
                header: t('Departure'),
                accessorFn: profile => profile.selectedStay?.endDay,
                cell: ({row}) => <DateElement date={row.original.selectedStay?.endDay} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    serverSortField: 'activeStays.endDay',
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.selectedStay?.endDay} dateOnly/>
                }
            },
            {
                id: 'academicTitle',
                header: t('Academic title'),
                accessorFn: profile => profile.academicTitle,
                cell: ({row}) => <Box>{row.original.academicTitle ?? ''}</Box>,
                size: 160,
                meta: {defaultActive: false, isServerSortable: true, printable: true}
            },
            {
                id: 'languages',
                header: t('Languages'),
                accessorFn: profile => profile.languages,
                cell: ({row}) => <LanguagesElement languageCodes={row.original.languages}/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'languageCode',
                    printable: true,
                    printCell: row => <LanguagesElement languageCodes={row.original.languages} asPrint/>
                }
            },
            {
                id: 'namePrefix',
                header: t('Name prefix'),
                accessorFn: profile => profile.namePrefix,
                cell: ({row}) => <Box>{row.original.namePrefix ?? ''}</Box>,
                size: 150,
                meta: {defaultActive: false, isServerSortable: true, printable: true}
            },
            {
                id: 'email',
                header: t('Email'),
                accessorFn: profile => profile.emails,
                cell: ({row}) => <Box>{row.original.emails.join(', ')}</Box>,
                size: 260,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'emails.email',
                    printable: true,
                    printCell: row => row.original.emails.join(', ')
                }
            },
            {
                id: 'primaryPhone',
                header: t('Phone'),
                accessorFn: profile => profile.primaryPhone,
                cell: ({row}) => <Box>{row.original.primaryPhone ?? ''}</Box>,
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'phones.phoneNumberFull',
                    printable: true
                }
            },
            {
                id: 'companyName',
                header: t('Company'),
                accessorFn: profile => profile.companyName,
                cell: ({row}) => <Box>{row.original.companyName ?? ''}</Box>,
                size: 210,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'company.companyName',
                    printable: true
                }
            },
            {
                id: 'totalStays',
                header: t('Total stays'),
                accessorFn: profile => profile.totalStays,
                cell: ({row}) => <Box>{row.original.totalStays ?? ''}</Box>,
                size: 130,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.stays.totalStays',
                    printable: true
                }
            },
            {
                id: 'totalNights',
                header: t('Total nights'),
                accessorFn: profile => profile.totalNights,
                cell: ({row}) => <Box>{row.original.totalNights ?? ''}</Box>,
                size: 130,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.stays.totalNights'
                }
            },
            {
                id: 'lastStayDate',
                header: t('Last stay'),
                accessorFn: profile => profile.lastStayDate,
                cell: ({row}) => <DateElement date={row.original.lastStayDate} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.stays.lastStayDate',
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.lastStayDate} dateOnly/>
                }
            },
            {
                id: 'nextStayDate',
                header: t('Next stay'),
                accessorFn: profile => profile.nextStayDate,
                cell: ({row}) => <DateElement date={row.original.nextStayDate} dateOnly/>,
                size: 150,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    serverSortField: 'smartData.stays.nextStayDate',
                    printable: true,
                    printCell: row => <DateElement date={row.original.nextStayDate} dateOnly/>
                }
            },
            {
                id: 'receiveNewsletters',
                header: t('Receives newsletters'),
                accessorFn: profile => profile.receiveNewsletters,
                cell: ({getValue}) => {
                    const value = getValue<boolean | undefined>();
                    return <Box>{value === undefined ? '' : t(value ? 'Yes' : 'No')}</Box>;
                },
                size: 190,
                meta: {
                    defaultActive: false,
                    isServerSortable: true,
                    printable: true,
                    printCell: row => {
                        const value = row.getValue<boolean | undefined>('receiveNewsletters');
                        return value === undefined ? '' : t(value ? 'Yes' : 'No');
                    }
                }
            },
            {
                id: 'primaryProvider',
                header: t('Source'),
                accessorFn: profile => profile.primaryProvider,
                cell: ({row}) => <Box>{row.original.primaryProvider ?? ''}</Box>,
                size: 170,
                meta: {defaultActive: false, isServerSortable: true, printable: true}
            },
            {
                id: 'insertDate',
                header: t('Created'),
                accessorFn: profile => profile.insertDate,
                cell: ({row}) => <DateElement date={row.original.insertDate}/>,
                size: 190,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.insertDate}/>
                }
            },
            {
                id: 'updateDate',
                header: t('Last updated'),
                accessorFn: profile => profile.updateDate,
                cell: ({row}) => <DateElement date={row.original.updateDate}/>,
                size: 190,
                meta: {
                    defaultActive: true,
                    isServerSortable: true,
                    printable: true,
                    printDefault: true,
                    printCell: row => <DateElement date={row.original.updateDate}/>
                }
            },
            {
                id: 'id',
                header: t('ID'),
                accessorFn: profile => profile.id,
                cell: ({row}) => <Box>{row.original.id}</Box>,
                size: 260,
                meta: {defaultActive: false, isServerSortable: true}
            }];

        return columns.filter(column => (
            !readOnlyUser || (column.id !== 'email' && column.id !== 'primaryPhone')
        ));
    }, [locale, readOnlyUser, t]);
};

const ProfilesList = () => {
    useTrackScreenVisit('system profiles');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const readOnlyUser = useReadOnly();
    const columns = useProfileListColumns(readOnlyUser);
    const [stayPeriod, setStayPeriod] = useState<ProfileStayPeriod>('ALL');

    const controller = useServerTable<ProfileListItem>({
        tableId: 'system-profiles',
        columns,
        getRowId: profile => profile.id,
        onRowClick: profile => navigate(`/system/profiles/${profile.id}`),
        initialSorting: [{id: 'insertDate', desc: true}],
        queryDependencies: [stayPeriod],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => {
            const today = new Date();
            const selectedDateRange = {
                start: startOfDay(today),
                end: endOfDay(today)
            };
            const serverSort = sorting
                ? [
                    sorting,
                    ...(sorting.field === 'insertDate'
                        ? [{field: 'id', direction: sorting.direction}]
                        : [])
                ]
                : undefined;

            return ProfileAggregationService.listProfiles({
                query: searchQuery || undefined,
                followMerge: true,
                overlaps: stayPeriod === 'STAYING' ? selectedDateRange : undefined,
                expectedStart: stayPeriod === 'CHECKING_IN' ? selectedDateRange : undefined,
                expectedEnd: stayPeriod === 'CHECKING_OUT' ? selectedDateRange : undefined,
                advancedFilter: filterRules.length ? {
                    filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
                } : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: serverSort
                }
            });
        }
    });
    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery
    ): ProfileListRequestDTO => {
        const today = new Date();
        const selectedDateRange = {
            start: startOfDay(today),
            end: endOfDay(today)
        };

        return {
            query: searchQuery || undefined,
            followMerge: true,
            overlaps: stayPeriod === 'STAYING' ? selectedDateRange : undefined,
            expectedStart: stayPeriod === 'CHECKING_IN' ? selectedDateRange : undefined,
            expectedEnd: stayPeriod === 'CHECKING_OUT' ? selectedDateRange : undefined,
            advancedFilter: filterRules.length ? {
                filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
            } : undefined,
            limit: {
                ...serverTableExportLimit(sorting),
                // Ties between equal creation dates break by id, as on screen
                sort: sorting
                    ? [
                        sorting,
                        ...(sorting.field === 'insertDate'
                            ? [{field: 'id', direction: sorting.direction}]
                            : [])
                    ]
                    : undefined
            }
        };
    }, [stayPeriod]);

    const filterOptionsQuery = useQuery(
        ['server-table-profile-filter-options'],
        () => ProfileService.getFilterOptions(),
        {staleTime: Infinity}
    );

    return (
        <SettingsListPage title={t('Profiles')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ProfileStayPeriodFilter value={stayPeriod} onChange={setStayPeriod}/>

                        <ServerTableSearch placeholder={t('Search profiles')}/>
                        <ServerTableSort/>
                        <ServerTableFilter
                            options={filterOptionsQuery.data ?? []}
                            disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                        />
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Profiles')}/>
                        <ServerTableExport list="profiles" title={t('Profiles')} buildRequest={exportRequest}/>

                        <ServerTableCreate
                            label={t('Create profile')}
                            disabled={readOnlyUser}
                            onClick={() => navigate('/system/profiles/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ProfilesList;
